// Error display component with user-friendly messaging
import React, { useState } from 'react';
import { AppError, ErrorRecoveryStrategy } from '../../types/errors';
import { getRecoveryStrategies } from '../../utils/errorHandler';
import { getNetworkStatus } from '../../utils/networkManager';
import './ErrorDisplay.css';

interface ErrorDisplayProps {
  error: AppError;
  level?: 'page' | 'component' | 'critical';
  onRetry?: () => void;
  onReload?: () => void;
  onDismiss?: () => void;
  showDetails?: boolean;
  className?: string;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  level = 'component',
  onRetry,
  onReload,
  onDismiss,
  showDetails = false,
  className = '',
}) => {
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  
  const recoveryStrategies = getRecoveryStrategies(error);
  const networkStatus = getNetworkStatus();

  const handleRecoveryAction = (strategy: ErrorRecoveryStrategy) => {
    switch (strategy.type) {
      case 'retry':
        if (onRetry) {
          onRetry();
        } else if (strategy.action) {
          strategy.action();
        }
        break;
      case 'fallback':
        if (strategy.action) {
          strategy.action();
        }
        break;
      case 'redirect':
        if (strategy.action) {
          strategy.action();
        } else {
          // Default redirect to home
          window.location.href = '/';
        }
        break;
      case 'manual':
        // Manual actions don't have automatic handlers
        break;
    }
  };

  const handleSendFeedback = () => {
    // In a real implementation, this would send feedback to support
    setFeedbackSent(true);
    console.log('Feedback sent for error:', error.code);
  };

  const getErrorIcon = () => {
    switch (error.severity) {
      case 'critical':
        return '🚨';
      case 'high':
        return '⚠️';
      case 'medium':
        return '⚡';
      case 'low':
        return 'ℹ️';
      default:
        return '❗';
    }
  };

  const getErrorTitle = () => {
    switch (level) {
      case 'critical':
        return 'Something went wrong';
      case 'page':
        return 'Page error';
      case 'component':
        return 'Oops!';
      default:
        return 'Error';
    }
  };

  const containerClasses = [
    'error-display',
    `error-display--${level}`,
    `error-display--${error.severity}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} role="alert">
      <div className="error-display__content">
        {/* Error Icon and Title */}
        <div className="error-display__header">
          <span className="error-display__icon" aria-hidden="true">
            {getErrorIcon()}
          </span>
          <h2 className="error-display__title">
            {getErrorTitle()}
          </h2>
        </div>

        {/* User-friendly message */}
        <div className="error-display__message">
          <p>{error.userMessage}</p>
        </div>

        {/* Network status indicator */}
        {!networkStatus.isOnline && (
          <div className="error-display__network-status">
            <span className="error-display__network-icon">📶</span>
            <p>You appear to be offline. Please check your internet connection.</p>
          </div>
        )}

        {/* Suggestions */}
        {error.suggestions && error.suggestions.length > 0 && (
          <div className="error-display__suggestions">
            <h3>Here's what you can try:</h3>
            <ul>
              {error.suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Recovery Actions */}
        {recoveryStrategies.length > 0 && (
          <div className="error-display__actions">
            {recoveryStrategies.map((strategy, index) => (
              <button
                key={index}
                type="button"
                className={`error-display__action error-display__action--${strategy.type}`}
                onClick={() => handleRecoveryAction(strategy)}
                title={strategy.description}
              >
                {strategy.label}
              </button>
            ))}
            
            {/* Additional common actions */}
            {onReload && level === 'page' && (
              <button
                type="button"
                className="error-display__action error-display__action--reload"
                onClick={onReload}
              >
                Refresh Page
              </button>
            )}
            
            {onDismiss && level === 'component' && (
              <button
                type="button"
                className="error-display__action error-display__action--dismiss"
                onClick={onDismiss}
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {/* Feedback Section */}
        <div className="error-display__feedback">
          {!feedbackSent ? (
            <div>
              <p className="error-display__feedback-text">
                Is this error preventing you from getting help? Let us know!
              </p>
              <button
                type="button"
                className="error-display__feedback-button"
                onClick={handleSendFeedback}
              >
                Report Problem
              </button>
            </div>
          ) : (
            <p className="error-display__feedback-sent">
              ✅ Thank you! We've received your report and will look into it.
            </p>
          )}
        </div>

        {/* Technical Details (collapsible) */}
        {showDetails && (
          <div className="error-display__details">
            <button
              type="button"
              className="error-display__details-toggle"
              onClick={() => setShowFullDetails(!showFullDetails)}
              aria-expanded={showFullDetails}
            >
              {showFullDetails ? 'Hide' : 'Show'} Technical Details
            </button>
            
            {showFullDetails && (
              <div className="error-display__details-content">
                <dl>
                  <dt>Error Code:</dt>
                  <dd>{error.code}</dd>
                  
                  <dt>Category:</dt>
                  <dd>{error.category}</dd>
                  
                  <dt>Severity:</dt>
                  <dd>{error.severity}</dd>
                  
                  <dt>Time:</dt>
                  <dd>{new Date(error.timestamp).toLocaleString()}</dd>
                  
                  {error.context?.url && (
                    <>
                      <dt>Page:</dt>
                      <dd>{error.context.url}</dd>
                    </>
                  )}
                  
                  <dt>Technical Message:</dt>
                  <dd>{error.message}</dd>
                </dl>
              </div>
            )}
          </div>
        )}

        {/* Help Text */}
        <div className="error-display__help">
          <p>
            <strong>Need more help?</strong> If this problem continues, try refreshing 
            the page or contact support. We're here to help you get back to using 
            the app smoothly.
          </p>
        </div>
      </div>
    </div>
  );
};